import { supabase } from "@/integrations/supabase/client";
import { Order } from "@/types/orders";
import { fetchOrderItems, fetchMenuItemImages } from "./orderItemsService";

/**
 * Get active orders for a specific table
 * @param tableId - The table ID to get orders for
 * @returns Promise with array of active orders
 */
export const getActiveOrdersForTable = async (
  tableId: string
): Promise<Order[]> => {
  try {
    // First get the orders for this table
    const { data: ordersData, error: ordersError } = await supabase
      .from("orders")
      .select("*")
      .eq("table_id", tableId)
      .not("status", "in", "(cancelled)");

    if (ordersError) {
      console.error("Error fetching orders:", ordersError);
      throw ordersError;
    }

    if (!ordersData || ordersData.length === 0) {
      return [];
    }

    // Process each order to include its items
    const orders = await Promise.all(
      ordersData.map(async (order) => {
        // Fetch order items for this specific order
        const { data: itemsData, error: itemsError } = await supabase
          .from("order_items")
          .select("*")
          .eq("order_id", order.id);

        if (itemsError) {
          console.error(
            `Error fetching items for order ${order.id}:`,
            itemsError
          );
          return {
            id: order.id,
            store_id: order.store_id,
            table_id: order.table_id,
            tableNumber: order.table_number,
            tableName: order.table_name,
            status: order.status,
            totalAmount: order.total_amount,
            items: [],
            specialInstructions: order.special_instructions,
            createdAt: order.created_at,
            updatedAt: order.updated_at,
            is_payed: order.is_payed,
          };
        }

        // Fetch images for menu items if we have item data
        const menuItemIds =
          itemsData
            ?.filter((item) => item.menu_item_id)
            .map((item) => item.menu_item_id) || [];

        let imageMap = new Map();
        if (menuItemIds.length > 0) {
          const { data: menuItems, error: menuError } = await supabase
            .from("menu_items")
            .select("id, image_url")
            .in("id", menuItemIds);

          if (menuError) {
            console.error("Error fetching menu item images:", menuError);
          } else if (menuItems) {
            imageMap = new Map(
              menuItems.map((item) => [item.id, item.image_url])
            );
          }
        }

        // Format the items with images
        const formattedItems =
          itemsData?.map((item) => {
            const imageUrl = item.menu_item_id
              ? imageMap.get(item.menu_item_id)
              : undefined;

            return {
              id: item.id,
              order_id: item.order_id,
              menu_item_id: item.menu_item_id,
              menu_item_name: item.menu_item_name,
              price: Number(item.price),
              quantity: Number(item.quantity),
              notes: item.notes,
              created_at: item.created_at,
              updated_at: item.updated_at,
              imageUrl,
            };
          }) || [];

        // Calculate the actual total from item prices and quantities
        const calculatedTotal = formattedItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        );

        // Return the complete order with its items
        return {
          id: order.id,
          store_id: order.store_id,
          table_id: order.table_id,
          tableNumber: order.table_number,
          tableName: order.table_name,
          status: order.status,
          totalAmount: order.total_amount || calculatedTotal,
          items: formattedItems,
          specialInstructions: order.special_instructions,
          createdAt: order.created_at,
          updatedAt: order.updated_at,
          is_payed: order.is_payed,
        };
      })
    );

    return orders;
  } catch (error) {
    console.error("Error in getActiveOrdersForTable:", error);
    throw error;
  }
};

/**
 * Cancel an order
 * @param orderId - The order ID to cancel
 * @returns Promise with success status
 */
export const cancelOrder = async (
  orderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Update the order status to cancelled
    const { error } = await supabase
      .from("orders")
      .update({ status: "cancelled" })
      .eq("id", orderId);

    if (error) {
      console.error("Error cancelling order:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in cancelOrder:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Unknown error cancelling order",
    };
  }
};
